import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  LoadingSpinner,
  Typography,
  StackedList,
  UserAlertListDataItem,
} from '@ecdlink/ui';
import { getAvatarColor } from '@ecdlink/core';
import { userSelectors } from '@/store/user';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { InviteService } from '@/services/InviteService';
import { InviteDto } from '@ecdlink/core/src/models/dto/Invite/invite.dto';
import { authSelectors } from '@/store/auth';
import { useHistory } from 'react-router-dom';
import ROUTES from '@routes/routes';

export const InvitesList = () => {
  const [invites, setInvites] = useState<InviteDto[]>([]);
  const [loading, setLoading] = useState(true);
  const user = useSelector(userSelectors.getUser);
  const { isOnline } = useOnlineStatus();
  const userAuth = useSelector(authSelectors.getAuthUser);
  const history = useHistory();

  useEffect(() => {
    const fetchInvites = async () => {
      if (!user?.id || !isOnline) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const inviteService = new InviteService(userAuth?.auth_token!);
        const data = await inviteService.getInvitesByPrincipalId(user.id);
        setInvites(data);
      } catch (error) {
        console.error('Failed to fetch invites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvites();
  }, [user?.id, isOnline]);

  if (invites.length === 0) {
    return (
      <div className="w-11/12">
        <Typography type="h1" text="No Invites" color="textDark" />
        <Typography
          type="body"
          text="You haven't sent any invites yet."
          color="textMid"
          className="mt-2"
        />
      </div>
    );
  }

  const inviteListItems: UserAlertListDataItem[] = invites.map((invite) => ({
    id: invite.id,
    title: `${invite.practitioner?.user?.firstName || ''} ${
      invite.practitioner?.user?.surname || ''
    }`,
    subTitle: invite.status,
    profileText: `${invite.practitioner?.user?.firstName?.[0] || ''}${
      invite.practitioner?.user?.surname?.[0] || ''
    }`,
    alertSeverity: 'error',
    avatarColor: getAvatarColor() || '',
    onActionClick: () => {
      history.push(ROUTES.Invite, { inviteDto: invite });
    },
  }));

  return (
    <div className="w-11/12">
      <StackedList listItems={inviteListItems} type="UserAlertList" />
    </div>
  );
};
